export interface FilePreview {
  name: string;
  size: number;
  preview: string;
}

// blob: URLs inherit this app's origin, so only inline-preview types that can't run as script here
const INLINE_PREVIEWABLE_TYPES = [
  "application/pdf",
  "text/plain",
  "application/json",
];

// office/document types download rather than render, so they're safe to open even without inline-preview eligibility
const DOWNLOADABLE_DOCUMENT_TYPES = [
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/zip",
];

// image subtypes carboncal-be/src/helpers/constants.ts currently allows, minus svg+xml which Chrome executes as script from a blob: URL
const NEW_TAB_ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
];

// audio subtypes carboncal-be/src/helpers/constants.ts currently allows; canPreviewInline reads this array directly now, no prefix to keep in sync
const NEW_TAB_ALLOWED_AUDIO_TYPES = [
  "audio/mpeg",
  "audio/wav",
  "audio/ogg",
  "audio/mp3",
  "audio/mp4",
  "audio/webm",
];

// video subtypes carboncal-be/src/helpers/constants.ts currently allows; canPreviewInline reads this array directly now, no prefix to keep in sync
const NEW_TAB_ALLOWED_VIDEO_TYPES = [
  "video/mp4",
  "video/mpeg",
  "video/quicktime",
  "video/webm",
  "video/x-msvideo",
];

// exported so the allowlist-completeness test diffs against this array itself, not a hand copy that can silently drift
export const NEW_TAB_ALLOWED_TYPES = [
  ...INLINE_PREVIEWABLE_TYPES,
  ...DOWNLOADABLE_DOCUMENT_TYPES,
  ...NEW_TAB_ALLOWED_IMAGE_TYPES,
  ...NEW_TAB_ALLOWED_AUDIO_TYPES,
  ...NEW_TAB_ALLOWED_VIDEO_TYPES,
  "application/octet-stream",
];

// browsers strip MIME parameters before dispatch, so normalise the same way before list comparisons
const normalizeMimeType = (mimeType: string): string =>
  mimeType.toLowerCase().split(";")[0].trim();

const FileUtils = {
  formatFileSize: (bytes: number): string =>
    bytes >= 1024 * 1024
      ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
      : `${(bytes / 1024).toFixed(1)} KB`,
  getBase64: (file: File) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        let base64 = reader.result as string;

        base64 = base64.replace("data", file.name);

        resolve(base64);
      };
      reader.onerror = reject;
    });
  },
  base64ToFile: (text: string): File => {
    // Format: "filename:mime/type;base64,<data>" (produced by getBase64)
    // Use ";base64," as anchor to safely handle filenames containing ":" or ","
    const markerIdx = text.indexOf(";base64,");
    const colonIdx = text.lastIndexOf(":", markerIdx);

    const filename = text.substring(0, colonIdx);
    const mimeType = text.substring(colonIdx + 1, markerIdx);
    const base64 = text.substring(markerIdx + 8);

    const byteString = atob(base64);

    const byteArray = new Uint8Array(byteString.length);

    for (let i = 0; i < byteString.length; i++) {
      byteArray[i] = byteString.charCodeAt(i);
    }

    return new File([byteArray], filename, { type: mimeType });
  },
  canPreviewInline: (mimeType: string): boolean => {
    // Chrome dispatches on the LAST value of a comma-separated Content-Type, so a raw comma anywhere is a smuggled type
    if (mimeType.includes(",")) return false;
    const normalized = normalizeMimeType(mimeType);
    return (
      INLINE_PREVIEWABLE_TYPES.includes(normalized) ||
      NEW_TAB_ALLOWED_AUDIO_TYPES.includes(normalized) ||
      NEW_TAB_ALLOWED_VIDEO_TYPES.includes(normalized)
    );
  },
  // explicit subtypes only, no family prefixes — a type earns access by exact membership, never by spelling
  canOpenInNewTab: (mimeType: string): boolean => {
    // Chrome dispatches on the LAST value of a comma-separated Content-Type, so a raw comma anywhere is a smuggled type
    if (mimeType.includes(",")) return false;
    const normalized = normalizeMimeType(mimeType);
    return NEW_TAB_ALLOWED_TYPES.includes(normalized);
  },
};

export default FileUtils;
